<template>
  <div class="main-wrap about-page">
    <main-header />

    <!-- 1. Apple-style full-viewport Hero -->
    <about-hero />

    <!-- 2. Philosophy — dark glassmorphism -->
    <about-philosophy />

    <!-- 3. Impact Numbers -->
    <about-impact />

    <!-- 4. Video + "Why Motanamy?" -->
    <section class="ab-section">
      <v-container>
        <div class="ab-panel ab-panel--lg">
          <v-row align="center">
            <v-col md="6" cols="12" class="px-md-6">
              <about-video />
            </v-col>
            <v-col md="6" cols="12" class="px-md-6">
              <div class="why-content">
                <p class="why-eyebrow">{{ $t('about.about') }}</p>
                <h2 class="why-title">{{ $t('about.whyChooseUs') }}</h2>
                <p class="why-body">{{ $t('about.whyChooseUsDesc') }}</p>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </section>

    <!-- 5. Mission & Vision -->
    <section class="ab-section ab-section--tinted">
      <v-container>
        <div class="ab-panel">
          <mission-vision />
        </div>
      </v-container>
    </section>

    <!-- 6. Values -->
    <section class="ab-section">
      <v-container>
        <div class="ab-panel">
          <about-values />
        </div>
      </v-container>
    </section>

    <!-- 7. Services -->
    <section class="ab-section ab-section--tinted">
      <v-container>
        <div class="ab-panel">
          <services-overview />
        </div>
      </v-container>
    </section>

    <!-- 8. Skills / Capabilities -->
    <section class="ab-section">
      <v-container>
        <div class="ab-panel ab-panel--accented">
          <about-progress />
        </div>
      </v-container>
    </section>

    <!-- 9. CTA -->
    <about-cta />

    <div id="footer">
      <main-footer />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~/assets/pages";

// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Section blocks ────────────────────────────────────────────────────
.ab-section {
  padding: 80px 0;

  &--tinted {
    background: rgba($blue, 0.025);

    .theme--dark & {
      background: rgba($blue, 0.06);
    }
  }
}

// ── Shared panel ─────────────────────────────────────────────────────
.ab-panel {
  border: 1px solid rgba($blue, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 12px 40px rgba($blue, 0.06);
  padding: 40px 32px;
  position: relative;
  overflow: hidden;

  .theme--dark & {
    background: rgba(20, 20, 35, 0.85);
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  &--lg {
    padding: 48px 40px;
  }

  &--accented {
    &::before {
      content: '';
      position: absolute;
      inset: 0 0 auto 0;
      height: 3px;
      background: linear-gradient(90deg, $blue, $purple, $red);
    }
  }
}

// ── Video + Why section ───────────────────────────────────────────────
.why-content {
  padding: 16px 0;
}

.why-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 16px;

  .theme--dark & {
    color: lighten($blue, 28%);
  }
}

.why-title {
  font-size: clamp(1.7rem, 4vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: #1a1a2e;
  margin-bottom: 18px;

  .theme--dark & {
    color: #f0f0f0;
  }
}

.why-body {
  font-size: 1.05rem;
  line-height: 1.8;
  color: #666;

  .theme--dark & {
    color: #999;
  }
}

// ── Responsive ───────────────────────────────────────────────────────
@media (max-width: 960px) {
  .ab-section {
    padding: 56px 0;
  }

  .ab-panel {
    padding: 28px 20px;

    &--lg {
      padding: 32px 20px;
    }
  }
}

@media (max-width: 600px) {
  .ab-section {
    padding: 40px 0;
  }

  .ab-panel {
    border-radius: 16px;
    padding: 20px 14px;
  }
}
</style>

<script>
import Header from "~/components/Header"
import HeroSection from "~/components/About/HeroSection"
import Philosophy from "~/components/About/Philosophy"
import ImpactNumbers from "~/components/About/ImpactNumbers"
import Video from "~/components/About/Video"
import MissionVision from "~/components/About/MissionVision"
import Values from "~/components/About/Values"
import ServicesOverview from "~/components/About/ServicesOverview"
import Progress from "~/components/About/Progress"
import CtaSection from "~/components/About/CtaSection"
import Footer from "~/components/Footer"

export default {
  components: {
    "main-header": Header,
    "main-footer": Footer,
    "about-hero": HeroSection,
    "about-philosophy": Philosophy,
    "about-impact": ImpactNumbers,
    "about-video": Video,
    "mission-vision": MissionVision,
    "about-values": Values,
    "services-overview": ServicesOverview,
    "about-progress": Progress,
    "about-cta": CtaSection
  },
  head() {
    const title = this.$t("seo.about.title");
    const desc = this.$t("seo.about.desc");
    const aboutSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": title,
      "description": desc,
      "url": "https://www.motanamy.com/about",
      "publisher": {
        "@type": "Organization",
        "name": "Motanamy",
        "url": "https://www.motanamy.com",
        "logo": "https://www.motanamy.com/images/logo.png",
      },
    };
    return {
      title,
      meta: [
        { hid: "description", name: "description", content: desc },
        { hid: "og:title", property: "og:title", content: title },
        { hid: "og:description", property: "og:description", content: desc },
        { hid: "og:url", property: "og:url", content: `https://www.motanamy.com${this.$route.path}` },
        { hid: "twitter:title", name: "twitter:title", content: title },
        { hid: "twitter:description", name: "twitter:description", content: desc },
      ],
      __dangerouslyDisableSanitizers: ["script"],
      script: [
        {
          hid: "about-schema",
          type: "application/ld+json",
          innerHTML: JSON.stringify(aboutSchema),
        },
      ],
    };
  }
}
</script>
